// src/validators/deal.ts
import { z } from "zod";
var dealCreateSchema = z.object({
  title: z.string().min(1).max(255),
  companyName: z.string().max(255).optional(),
  contactName: z.string().max(255).optional(),
  contactEmail: z.string().email().optional(),
  amount: z.number().nonnegative().optional(),
  currency: z.string().length(3).default("USD"),
  stage: z.string().optional(),
  probability: z.number().int().min(0).max(100).optional(),
  expectedClose: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  sourcedBy: z.string().optional(),
  portcoId: z.string().max(100).optional(),
  nextStep: z.string().max(500).optional(),
  source: z.string().max(100).optional(),
  notes: z.string().max(5e3).optional(),
  metadata: z.record(z.unknown()).optional()
});
var dealUpdateSchema = z.object({
  title: z.string().min(1).max(255).optional(),
  companyName: z.string().max(255).nullable().optional(),
  contactName: z.string().max(255).nullable().optional(),
  contactEmail: z.string().email().nullable().optional(),
  amount: z.number().nonnegative().nullable().optional(),
  currency: z.string().length(3).optional(),
  stage: z.string().optional(),
  probability: z.number().int().min(0).max(100).nullable().optional(),
  expectedClose: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  nextStep: z.string().max(500).nullable().optional(),
  source: z.string().max(100).nullable().optional(),
  notes: z.string().max(5e3).nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional()
});
var dealListSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().min(1).max(100).default(25),
  stage: z.string().optional(),
  ownerId: z.string().optional(),
  search: z.string().optional(),
  sortBy: z.enum(["createdAt", "updatedAt", "amount", "expectedClose", "title"]).default("updatedAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var dealCloseSchema = z.object({
  closedWon: z.boolean(),
  actualClose: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  notes: z.string().max(5e3).optional()
});

// src/validators/commission.ts
import { z as z2 } from "zod";
var commissionTierSchema = z2.object({
  name: z2.string().min(1),
  minRevenue: z2.number().nonnegative(),
  maxRevenue: z2.number().positive().nullable(),
  rate: z2.number().min(0).max(1),
  isIncremental: z2.boolean()
});
var flatPlanSchema = z2.object({
  type: z2.literal("flat"),
  rate: z2.number().min(0).max(1)
});
var tieredPlanSchema = z2.object({
  type: z2.literal("tiered"),
  tiers: z2.array(commissionTierSchema).min(1)
});
var teamQuotaPlanSchema = z2.object({
  type: z2.literal("team_quota"),
  teamQuota: z2.number().positive(),
  individualBase: z2.number().min(0).max(1),
  teamBonus: z2.number().min(0).max(1),
  tiers: z2.array(commissionTierSchema).optional()
});
var acceleratorPlanSchema = z2.object({
  type: z2.literal("accelerator"),
  baseRate: z2.number().min(0).max(1),
  quota: z2.number().positive(),
  accelerators: z2.array(
    z2.object({
      name: z2.string().min(1),
      quotaPercent: z2.number().positive(),
      multiplier: z2.number().positive()
    })
  ).min(1)
});
var commissionPlanConfigSchema = z2.discriminatedUnion("type", [
  flatPlanSchema,
  tieredPlanSchema,
  teamQuotaPlanSchema,
  acceleratorPlanSchema
]);
var commissionPlanCreateSchema = z2.object({
  name: z2.string().min(1).max(255),
  description: z2.string().max(1e3).optional(),
  config: commissionPlanConfigSchema,
  baseCurrency: z2.string().length(3).default("CAD"),
  payoutCadence: z2.enum(["monthly", "quarterly", "annual"]).default("monthly"),
  effectiveFrom: z2.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  effectiveTo: z2.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional()
});
var commissionAssignmentSchema = z2.object({
  commissionPlanId: z2.string().min(1),
  userId: z2.string().min(1),
  effectiveFrom: z2.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  effectiveTo: z2.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional()
});
var commissionSimulateSchema = z2.object({
  planId: z2.string().min(1),
  revenue: z2.number().nonnegative(),
  currency: z2.string().length(3).default("CAD")
});

// src/validators/auth.ts
import { z as z3 } from "zod";
var loginSchema = z3.object({
  email: z3.string().email(),
  password: z3.string().min(8).max(128)
});
var signupSchema = z3.object({
  name: z3.string().min(1).max(100),
  email: z3.string().email(),
  password: z3.string().min(8).max(128),
  companyName: z3.string().min(1).max(255),
  slug: z3.string().min(3).max(50).regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, "Slug must be lowercase alphanumeric with hyphens")
});
var oauthCallbackSchema = z3.object({
  code: z3.string().min(1),
  state: z3.string().min(1),
  provider: z3.enum(["google", "github"])
});

// src/validators/settings.ts
import { z as z4 } from "zod";
var settingsUpdateSchema = z4.object({
  baseCurrency: z4.string().length(3).optional(),
  aiProvider: z4.enum(["claude", "workers-ai", "openai"]).optional(),
  companyName: z4.string().min(1).max(255).optional(),
  logoUrl: z4.string().url().nullable().optional(),
  fiscalYearStart: z4.number().int().min(1).max(12).optional(),
  // Sourcing split: percentage of commission on a deal allocated to the sourcer (0-100, default 20)
  sourcingSplitPercent: z4.number().min(0).max(100).optional()
});
var pipelineStagesUpdateSchema = z4.object({
  stages: z4.array(
    z4.object({
      id: z4.string().optional(),
      name: z4.string().min(1).max(100),
      slug: z4.string().min(1).max(50).regex(/^[a-z0-9_]+$/),
      position: z4.number().int().nonnegative(),
      probability: z4.number().int().min(0).max(100),
      isWon: z4.boolean().default(false),
      isLost: z4.boolean().default(false),
      color: z4.string().nullable().optional()
    })
  ).min(2).refine(
    (stages) => stages.some((s) => s.isWon),
    { message: "At least one stage must be marked as won" }
  ).refine(
    (stages) => stages.some((s) => s.isLost),
    { message: "At least one stage must be marked as lost" }
  )
});

// src/validators/team.ts
import { z as z5 } from "zod";
var inviteSchema = z5.object({
  email: z5.string().email(),
  role: z5.enum(["admin", "director", "associate", "portco_founder"])
});
var updateRoleSchema = z5.object({
  role: z5.enum(["admin", "director", "associate", "portco_founder"])
});

// src/constants/roles.ts
var ROLES = ["admin", "director", "associate", "portco_founder"];
var ROLE_LABELS = {
  admin: "Admin",
  director: "Director",
  associate: "Associate",
  portco_founder: "Portco Founder"
};
var ROLE_HIERARCHY = {
  admin: 100,
  director: 75,
  associate: 50,
  portco_founder: 25
};

// src/constants/currencies.ts
var SUPPORTED_CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "CAD", name: "Canadian Dollar", symbol: "CA$" },
  { code: "LKR", name: "Sri Lankan Rupee", symbol: "Rs" },
  { code: "GBP", name: "British Pound", symbol: "\xA3" },
  { code: "EUR", name: "Euro", symbol: "\u20AC" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$" }
];
var DEFAULT_BASE_CURRENCY = "CAD";

// src/constants/pipeline-defaults.ts
var DEFAULT_PIPELINE_STAGES = [
  {
    id: "stage_lead",
    name: "Lead",
    slug: "lead",
    position: 0,
    probability: 10,
    isWon: false,
    isLost: false,
    color: "#6B7280"
  },
  {
    id: "stage_prospect",
    name: "Prospect",
    slug: "prospect",
    position: 1,
    probability: 20,
    isWon: false,
    isLost: false,
    color: "#3B82F6"
  },
  {
    id: "stage_negotiating",
    name: "Negotiating",
    slug: "negotiating",
    position: 2,
    probability: 40,
    isWon: false,
    isLost: false,
    color: "#F59E0B"
  },
  {
    id: "stage_verbal",
    name: "Verbal",
    slug: "verbal",
    position: 3,
    probability: 70,
    isWon: false,
    isLost: false,
    color: "#8B5CF6"
  },
  {
    id: "stage_signed",
    name: "Signed",
    slug: "signed",
    position: 4,
    probability: 90,
    isWon: false,
    isLost: false,
    color: "#10B981"
  },
  {
    id: "stage_active",
    name: "Active",
    slug: "active",
    position: 5,
    probability: 100,
    isWon: true,
    isLost: false,
    color: "#059669"
  },
  {
    id: "stage_churned",
    name: "Churned",
    slug: "churned",
    position: 6,
    probability: 0,
    isWon: false,
    isLost: true,
    color: "#EF4444"
  }
];

// src/constants/commission-defaults.ts
var NV_COMMISSION_TIERS = [
  {
    name: "Tier 1",
    minRevenue: 0,
    maxRevenue: 13500,
    rate: 0.1,
    isIncremental: true
  },
  {
    name: "Tier 2",
    minRevenue: 13500,
    maxRevenue: 27e3,
    rate: 0.15,
    isIncremental: true
  },
  {
    name: "Tier 3",
    minRevenue: 27e3,
    maxRevenue: 40500,
    rate: 0.2,
    isIncremental: true
  },
  {
    name: "Tier 4",
    minRevenue: 40500,
    maxRevenue: 67500,
    rate: 0.22,
    isIncremental: true
  },
  {
    name: "Tier 5",
    minRevenue: 67500,
    maxRevenue: null,
    rate: 0.25,
    isIncremental: true
  }
];
var NV_COMMISSION_PLAN_CONFIG = {
  type: "tiered",
  tiers: NV_COMMISSION_TIERS
};
var NV_COMMISSION_PLAN = {
  name: "NV Standard Tiered Plan",
  description: "Nanthi Ventures standard incremental tiered commission plan with quarterly payouts",
  config: NV_COMMISSION_PLAN_CONFIG,
  baseCurrency: "CAD",
  payoutCadence: "quarterly"
};

// src/utils/currency.ts
function formatCurrency(amount, currency, locale = "en-US") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.ceil(amount));
}
function abbreviateAmount(amount, currency) {
  const info = SUPPORTED_CURRENCIES.find((c) => c.code === currency);
  const symbol = info?.symbol ?? currency;
  const absAmount = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  if (absAmount >= 1e9) {
    return `${sign}${symbol}${(absAmount / 1e9).toFixed(1)}B`;
  }
  if (absAmount >= 1e6) {
    return `${sign}${symbol}${(absAmount / 1e6).toFixed(1)}M`;
  }
  if (absAmount >= 1e3) {
    return `${sign}${symbol}${(absAmount / 1e3).toFixed(1)}K`;
  }
  return `${sign}${symbol}${absAmount.toFixed(0)}`;
}

// src/utils/date.ts
function formatDate(iso, locale = "en-US") {
  const date = new Date(iso);
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}
function formatRelative(iso) {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMs = now - then;
  const diffDays = Math.round(diffMs / (1e3 * 60 * 60 * 24));
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays === -1) return "tomorrow";
  if (diffDays > 0) {
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.round(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.round(diffDays / 30)} months ago`;
    return `${Math.round(diffDays / 365)} years ago`;
  }
  const absDays = Math.abs(diffDays);
  if (absDays < 7) return `in ${absDays} days`;
  if (absDays < 30) return `in ${Math.round(absDays / 7)} weeks`;
  if (absDays < 365) return `in ${Math.round(absDays / 30)} months`;
  return `in ${Math.round(absDays / 365)} years`;
}
function daysSince(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  return Math.floor(diffMs / (1e3 * 60 * 60 * 24));
}
function daysUntil(iso) {
  const diffMs = new Date(iso).getTime() - Date.now();
  return Math.floor(diffMs / (1e3 * 60 * 60 * 24));
}
function startOfQuarter(date = /* @__PURE__ */ new Date()) {
  const quarter = Math.floor(date.getMonth() / 3);
  return new Date(date.getFullYear(), quarter * 3, 1);
}
function endOfQuarter(date = /* @__PURE__ */ new Date()) {
  const quarter = Math.floor(date.getMonth() / 3);
  const endMonth = quarter * 3 + 3;
  return new Date(date.getFullYear(), endMonth, 0, 23, 59, 59, 999);
}
function isWithinTimeWindow(time, timezone, windowMinutes = 15) {
  const now = /* @__PURE__ */ new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
  const parts = formatter.formatToParts(now);
  const hourPart = parts.find((p) => p.type === "hour");
  const minutePart = parts.find((p) => p.type === "minute");
  if (!hourPart || !minutePart) return false;
  const currentMinutes = parseInt(hourPart.value, 10) * 60 + parseInt(minutePart.value, 10);
  const [targetHour, targetMinute] = time.split(":").map(Number);
  if (targetHour === void 0 || targetMinute === void 0) return false;
  const targetMinutes = targetHour * 60 + targetMinute;
  const diff = Math.abs(currentMinutes - targetMinutes);
  const wrappedDiff = Math.min(diff, 1440 - diff);
  return wrappedDiff <= windowMinutes;
}

// src/utils/id.ts
function generateId() {
  return crypto.randomUUID();
}
function generateToken(length = 32) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}
export {
  DEFAULT_BASE_CURRENCY,
  DEFAULT_PIPELINE_STAGES,
  NV_COMMISSION_PLAN,
  NV_COMMISSION_PLAN_CONFIG,
  NV_COMMISSION_TIERS,
  ROLES,
  ROLE_HIERARCHY,
  ROLE_LABELS,
  SUPPORTED_CURRENCIES,
  abbreviateAmount,
  commissionAssignmentSchema,
  commissionPlanCreateSchema,
  commissionSimulateSchema,
  commissionTierSchema,
  daysSince,
  daysUntil,
  dealCloseSchema,
  dealCreateSchema,
  dealListSchema,
  dealUpdateSchema,
  endOfQuarter,
  formatCurrency,
  formatDate,
  formatRelative,
  generateId,
  generateToken,
  inviteSchema,
  isWithinTimeWindow,
  loginSchema,
  oauthCallbackSchema,
  pipelineStagesUpdateSchema,
  settingsUpdateSchema,
  signupSchema,
  startOfQuarter,
  updateRoleSchema
};
//# sourceMappingURL=index.js.map